import { action, computed, makeObservable, observable } from 'mobx';
import { defaultGoogleTagManager } from '../constants/googleTagManager';
import { googleTagManagerCast } from '../helpers/googleTagManager';
import { IGoogleTagManager, IResponseGoogleTagManager } from '../types/googleTagManager';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';

class GoogleTagManagerStore {
  constructor() {
    makeObservable(this);
  }

  @observable public tagData: IGoogleTagManager = defaultGoogleTagManager;
  @observable public loadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public isEdit: boolean = false;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loadingStatus);
  }

  @action public init = () => {
    this.tagData = defaultGoogleTagManager;
    this.loadingStatus = LoadingStatus.Init;
    this.isEdit = false;

    this.fetchGoogleTagData();
  };

  @action private fetchGoogleTagData = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const settings: IResponseGoogleTagManager = await client.get('v3/integrations/google/gtm');

      this.tagData = googleTagManagerCast(settings);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'googleTagManagerStore fetchGoogleTagData' });
    }
  };

  @action public changeTagData = (data: Partial<IGoogleTagManager>) => {
    this.tagData = { ...this.tagData, ...data };
    this.isEdit = true;
  };

  @action public updateGoogleTagData = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const params = {
        google_tag_manager_head: this.tagData.head,
        google_tag_manager_body: this.tagData.body,
      };

      await client.post('v3/integrations/google/gtm', params);

      this.isEdit = false;
      this.loadingStatus = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'googleTagManagerStore updateGoogleTagData' });
    }
  };
}

const googleTagManagerStore = new GoogleTagManagerStore();

export default googleTagManagerStore;
